import { CircleAlert } from 'lucide-react';
import { debounce } from 'lodash';

import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LegalContextEnum, type IAgreementUpdateParams } from 'src/graphql/schema';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { legalContextOptionsRich as options } from '../model';

type Props = {
  onUpdate: (agreementParams: IAgreementUpdateParams) => void;
  onOtherChange: (value: string) => void;
  legalContext?: LegalContextEnum[];
  legalContextOther?: string;
};

const AgreementLegalContextSettings = ({ onUpdate, legalContext = [], onOtherChange, legalContextOther }: Props) => {
  const onLegalContextChange = (legalContext: LegalContextEnum[]) => onUpdate({ legalContext });

  const onLegalOtherChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => onOtherChange(event.target.value),
    500,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Legal context
          {!legalContext.length && (
            <span className="text-orange-700 flex items-center gap-1">
              <CircleAlert className="size-3.5" />
              <i className="text-xs font-medium">Required</i>
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Choose, or enter the title of the agreement between you and your counterpart. This is the legal contract
          outlining your relationship and obligations, and will be referenced in the escrow agreement.
          <br />
          <br />
          Choose one, or all that are applicable.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ToggleGroupCard
          type="multiple"
          className="flex-col w-full"
          onValueChange={onLegalContextChange}
          value={legalContext}
        >
          {options.map(({ title, value, description, icon }) => (
            <ToggleGroupCardItem key={value} value={value} icon={icon} title={title} description={description} />
          ))}
        </ToggleGroupCard>

        {legalContext.includes(LegalContextEnum.Other) && (
          <div className="mt-6">
            <Label htmlFor="legalContextOther">Your agreement title:</Label>
            <div className="relative">
              <Input
                id="legalContextOther"
                placeholder="Enter agreement title"
                onChange={onLegalOtherChange}
                defaultValue={legalContextOther || ''}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground pointer-events-none bg-white pl-2">
                Agreement
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgreementLegalContextSettings;
